/* =============================================================================
   Keep the session alive for as long as somebody is working, and end it when
   nobody is.

   THE PROBLEM. identity issues a 15-minute access token, and until this file
   the only thing that renewed one was a full page render (requireIdentityLogin
   in modules/main/controllers/default.ts). Every screen in this product is an
   SPA shell — switching panes is client-side — so a console or a till that
   nobody navigates away from never rendered a page again: about fifteen minutes
   in, every /api/* call it made came back 401, and the way back in was a trip
   through the IdP with a "Continue to application" button to press. Somebody
   in the middle of a sale was being asked to sign in again, on the clock, for
   no reason but the shape of the code.

   THE RULE, in two halves:
     · a screen with a human on it renews itself every ten minutes, silently,
       with no navigation and nothing on screen;
     · a screen nobody has touched for three hours is not renewed. Its session
       is ENDED (POST /auth/refresh does that server-side) and the browser is
       sent to /auth/idle to sign in again. An abandoned till is not a live
       session.

   Real input only — pointer, keys, wheel, touch. Not a timer, not a poll, not
   the tab merely being visible: a screen left on in an empty shop is visible
   all night and that is exactly the session the ceiling is there to close.

   The server holds the same ceiling and holds it on its own clock
   (helpers/session/idle.ts), so a tab whose JavaScript died or a machine that
   slept through the ceiling cannot come back and renew. What is reported from here
   is how long since the last input; what is DECIDED is decided there.
   ========================================================================== */
import { isIdentitySurface, currentPath, startAuthNavigation } from "./reauth.js";
/** Sent on every renewal — the CSRF guard on POST /auth/refresh (modules/auth/controllers). */
const KEEPALIVE_HEADER = "x-bo-keepalive";
/**
 * Defaults, replaced by the server's own numbers on the first successful renewal.
 *
 * They have to exist here because the first renewal is the one thing that happens BEFORE any
 * server answer, and they have to be replaceable because `SESSION_IDLE_MINUTES` lives in
 * `brandos.configuration` — retuning it should not mean waiting for every open till to reload.
 */
let renewAfterMs = 10 * 60 * 1000; // 15-minute token, 5 minutes of headroom
let idleLimitMs = 3 * 60 * 60 * 1000;
/** How often the clock is looked at. No network unless a renewal is actually due. */
const TICK_MS = 60 * 1000;
/** Cheap guard on `pointermove`, which fires hundreds of times a second on a moving mouse. */
const ACTIVITY_THROTTLE_MS = 5_000;
const now = () => Date.now();
let lastActivity = now();
let lastRenewAt = now();
let started = false;
let stopped = false;
let inFlight = null;
/** Milliseconds since the last real user input on this screen. */
export function idleMs() {
    return Math.max(0, now() - lastActivity);
}
/**
 * Mark the screen as touched. Exported so a surface with input this file cannot see — a barcode
 * gun read off a socket, a customer-facing mirror driven by the till next to it — can say so.
 */
export function noteActivity() {
    const t = now();
    if (t - lastActivity < ACTIVITY_THROTTLE_MS)
        return;
    lastActivity = t;
}
/* Both of these go through reauth.ts's one navigation, and that is the whole point of it being
 * there: a pane's 401 and this clock deciding in the same second used to be TWO trips to the IdP,
 * two authorization flows, and one of them was guaranteed to come back to a callback that could
 * not answer it. Whoever gets there first navigates; the other stands down. */
function goToLogin() {
    stopped = true;
    const path = currentPath() + String(globalThis.location?.search ?? "");
    // `prompt=none` — same reasoning as reauth.ts's 401 handler: renew without putting the
    // Continue-to-application page in front of someone who is mid-sale. Without it, every renewal
    // this clock decides on its own (a refused rotation, or repeated failures) landed on that page
    // exactly as the reported bug described. A silent attempt identity cannot satisfy comes back
    // to /auth/callback as `error=login_required`, which restarts the login interactively — see
    // that controller's `pkce?.silent` branch.
    startAuthNavigation(`/auth/login?next=${encodeURIComponent(path || "/")}&prompt=none`);
}
function goIdle() {
    stopped = true;
    const path = currentPath() + String(globalThis.location?.search ?? "");
    startAuthNavigation(`/auth/idle?next=${encodeURIComponent(path || "/")}`);
}
/**
 * Renew now, once — concurrent callers share the one request.
 *
 * The sharing is not an optimization. identity rotates a refresh token on every use and revokes
 * the whole family the moment a spent one is presented again, so two renewals racing each other
 * would look exactly like a stolen token and would end the session for real. The server holds
 * the same line for requests that get past this one (modules/auth/controllers/refresh.ts).
 *
 * Returns `skip` on a surface that does not sign in with identity — the mini-app shopper and the
 * member app hold different credentials entirely and have nothing to renew here.
 */
export async function renewNow() {
    if (!isIdentitySurface(currentPath()))
        return "skip";
    if (inFlight)
        return inFlight;
    const idle = idleMs();
    inFlight = (async () => {
        try {
            const res = await fetch("/auth/refresh", {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json", [KEEPALIVE_HEADER]: "1" },
                cache: "no-store",
                body: JSON.stringify({ idleMs: idle }),
            });
            const body = await res.json().catch(() => null);
            if (res.ok && body?.success) {
                lastRenewAt = now();
                const d = body.data ?? {};
                if (Number.isFinite(d.renewInSeconds) && d.renewInSeconds > 0)
                    renewAfterMs = d.renewInSeconds * 1000;
                if (Number.isFinite(d.idleLimitSeconds) && d.idleLimitSeconds > 0)
                    idleLimitMs = d.idleLimitSeconds * 1000;
                return "ok";
            }
            // The server decided this screen has been idle too long and has already ended the
            // session — there is nothing left to renew, and the browser's job is to go and say so.
            if (body?.error?.code === "IDLE_TIMEOUT")
                return "idle";
            // NO IDENTITY SESSION HERE, which is NOT the same as one that ended, and the difference
            // is a till that keeps working versus a till that walks itself to the IdP mid-sale.
            //
            // A staff terminal or POS enrolled with `bo_device` is admitted by its DEVICE
            // (modules/main/controllers/default.ts `deviceOk` — it short-circuits the identity gate
            // entirely, and src/index.ts lets its /api calls through the same way). Such a terminal
            // legitimately holds no `bo_refresh_token` at all, so this endpoint answers NO_SESSION
            // every time and nothing is wrong. Renewing is simply not this screen's business:
            // stand down, quietly and for good, rather than redirecting a cashier who is signed in
            // by every rule that actually applies to them.
            if (body?.error?.code === "NO_SESSION") {
                stopped = true;
                return "skip";
            }
            // Any other 401 is the server's considered answer about a session that DID exist: the
            // refresh token is spent, revoked, or the account is suspended. Nothing here can fix
            // that, so do not sit on it.
            if (res.status === 401)
                return "ended";
            return "failed";
        }
        catch {
            // Network/offline. NOT a failed session: leave `lastRenewAt` where it is so the next
            // tick tries again, and let a genuine 401 be the thing that ends a session.
            return "failed";
        }
        finally {
            inFlight = null;
        }
    })();
    return inFlight;
}
/** One pass of the clock: end an abandoned session, renew a live one, do nothing in between. */
async function tick() {
    if (stopped)
        return;
    // Re-checked here and not only in startKeepalive(): the idle branch below NAVIGATES, and a
    // shopper on the mini-app being thrown to the staff IdP for standing still is a far worse
    // failure than a missed renewal. The surface is a property of the page, so this cannot change
    // under a running tab — but the cost of asking is nothing and the cost of assuming is that.
    if (!isIdentitySurface(currentPath()))
        return;
    if (idleMs() > idleLimitMs) {
        goIdle();
        return;
    }
    if (now() - lastRenewAt < renewAfterMs)
        return;
    const result = await renewNow();
    if (result === "idle")
        goIdle();
    else if (result === "ended")
        goToLogin();
    else if (result === "failed" && now() - lastRenewAt > renewAfterMs * 2) {
        // Two renewal windows with nothing accepted: the token behind this screen has expired or
        // is about to, so stop pretending and go through the IdP while the person is still here
        // to be asked. A network blip does not reach this — it clears on the next tick.
        goToLogin();
    }
}
/**
 * A recursive setTimeout, not setInterval: a tick that is still waiting on the IdP must not have
 * the next one fire on top of it, and a background tab that the browser throttles should re-arm
 * from the tail of its own work rather than accumulate a queue of missed alarms. The perf budget
 * asserts this (test/perf-budget.test.mjs C7).
 */
function schedule() {
    setTimeout(() => {
        void tick().finally(() => { if (!stopped)
            schedule(); });
    }, TICK_MS);
}
/**
 * Start watching. Idempotent, and a no-op anywhere it does not belong: the mini-app is an
 * anonymous shopper on a `bo_surface` cookie and the member app is a customer on `mk_member` —
 * neither has an identity session, and renewing one on their behalf would be nonsense.
 */
export function startKeepalive() {
    if (started)
        return;
    const doc = globalThis.document;
    if (!doc || typeof globalThis.fetch !== "function")
        return; // SSR / test / no DOM
    if (!isIdentitySurface(currentPath()))
        return;
    started = true;
    // Capture phase and passive: this must see input that a pane's own handler stops from
    // bubbling, and it must never be the reason a scroll or a tap feels slow.
    for (const type of ["pointerdown", "pointermove", "keydown", "wheel", "touchstart"]) {
        doc.addEventListener(type, noteActivity, { capture: true, passive: true });
    }
    // Coming back to the tab is when a stale screen has to be dealt with, and it is the moment
    // the clock has most likely jumped: a laptop that slept for two hours wakes with its timers
    // hours behind. Checked here rather than waited for, so somebody returning to a dead session
    // is told immediately instead of up to a minute later.
    doc.addEventListener("visibilitychange", () => {
        if (doc.visibilityState === "visible")
            void tick();
    });
    schedule();
}
/** Test seam: the module keeps wall-clock state, so a case needs a way back to a known start. */
export function __resetKeepaliveForTest(at = now()) {
    lastActivity = at;
    lastRenewAt = at;
    started = false;
    stopped = false;
    inFlight = null;
    renewAfterMs = 10 * 60 * 1000;
    idleLimitMs = 3 * 60 * 60 * 1000;
}
/** Test seam: pretend the last input / last renewal happened this long ago. */
export function __setClocksForTest(v) {
    if (v.idleForMs != null)
        lastActivity = now() - v.idleForMs;
    if (v.renewedAgoMs != null)
        lastRenewAt = now() - v.renewedAgoMs;
}
/** Test seam: one pass of the clock, awaited. */
export function __tickForTest() {
    return tick();
}
//# sourceMappingURL=keepalive.js.map